import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Fetch avec timeout pour éviter les requêtes qui bloquent indéfiniment
const fetchWithTimeout = (url: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
  const timeout = 15000 // 15s max par requête
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  // Si le caller a déjà un signal, on combine les deux
  const existingSignal = options?.signal
  if (existingSignal?.aborted) {
    clearTimeout(timeoutId)
    return Promise.reject(new DOMException('Aborted', 'AbortError'))
  }

  if (existingSignal) {
    existingSignal.addEventListener('abort', () => controller.abort())
  }

  return fetch(url, {
    ...options,
    signal: controller.signal,
  }).finally(() => clearTimeout(timeoutId))
}

// Lock "no-op" : désactive le verrou inter-tabs de Supabase Auth.
// Par défaut, Supabase utilise navigator.locks (Web Locks API) pour
// synchroniser le refresh du token entre les tabs. Si un tab freeze
// (arrière-plan, réseau coupé), le lock bloque TOUTES les requêtes
// sur les autres tabs aussi. Avec le no-op lock, chaque tab gère
// son propre refresh indépendamment.
const noopLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  return fn()
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    lock: noopLock,
    lockAcquireTimeout: 5000, // Fallback: max 5s d'attente si le lock custom est ignoré
    // Passkeys (WebAuthn) : API expérimentale Supabase, opt-in obligatoire
    experimental: { passkey: true },
  },
  global: {
    fetch: fetchWithTimeout,
  },
})

// ─── Refresh proactif quand le tab redevient visible ───────────────
// Le navigateur freeze les timers/réseau pour les tabs en arrière-plan.
// Quand l'utilisateur revient, le token peut être expiré et le refresh
// automatique bloqué. On force un refresh immédiat.
let lastVisibilityRefresh = 0

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return

  const now = Date.now()
  // Pas plus d'un refresh toutes les 10 secondes
  if (now - lastVisibilityRefresh < 10000) return
  lastVisibilityRefresh = now

  supabase.auth.getSession().then(({ data: { session } }) => {
    if (!session) return

    // Si le token expire dans moins de 5 minutes, on force le refresh
    const expiresAt = session.expires_at
    if (expiresAt && expiresAt * 1000 - now < 5 * 60 * 1000) {
      supabase.auth.refreshSession().catch((err) => {
        console.warn('[Supabase] Erreur refresh session au retour sur le tab:', err)
      })
    }
  }).catch(() => {
    // Silencieux - pas de session = pas de refresh nécessaire
  })
})

// ─── Refresh proactif quand le réseau revient ──────────────────────
window.addEventListener('online', () => {
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (session) {
      supabase.auth.refreshSession().catch(() => {})
    }
  }).catch(() => {})
})
